import React, { useCallback, useState } from 'react';

import Button from '../../library/button/button';
import Typography from '../../library/typography/typography';
import VehicleDescription from '../../library/vehicle-description/vehicle-description';
import FitmentSizeGroup from '../fitment-size/components/fitment-size-group/fitment-size-group';
import {
  BellSolidIcon,
  ShoppingCartSolidIcon,
  TrashAltRegularIcon
} from '../../shared/icons/icons';
import Confirmation from '../confirmation/confirmation';
import FitmentSize from '../fitment-size/fitment-size';
import VehicleImage from '../vehicle-image/vehicle-image';

import {
  useCmsPageContentSlots,
  useModal,
  useTireWheelPackage
} from '../../shared/hooks/hooks';

import { Fitment } from '../../shared/types/fitment';
import { VEHICLE_CONFIRMATION } from '../../shared/constants/modals';

import './fitment-banner.scss';

type MaxSize = 'xs' | 'sm' | 'md' | 'lg';

interface FitmentBannerProps {
  className?: string;
  fitment: Fitment;
  hasItemsInCart?: boolean;
  hideFitmentSize?: boolean;
  hideVehicleImage?: boolean;
  incompleteFitmentMessage?: string;
  isDeleteLoading?: boolean;
  isPartialFitment?: boolean;
  isSaveVehicleDisabled?: boolean;
  maxSize?: MaxSize;
  onDelete?: () => void;
  onEditFitment?: () => void;
  onPromptDeleteConfirmation?: () => void;
  onSaveVehicle?: () => void;
  onShopProducts?: () => void;
  showAnimation?: boolean;
  showChangeVehicleLink?: boolean;
  showEditLink?: boolean;
}

function FitmentBanner(props: FitmentBannerProps) {
  const {
    className,
    fitment,
    hasItemsInCart,
    hideFitmentSize = false,
    hideVehicleImage = false,
    incompleteFitmentMessage,
    isDeleteLoading,
    isPartialFitment = false,
    isSaveVehicleDisabled = false,
    maxSize = '',
    onDelete,
    onEditFitment,
    onPromptDeleteConfirmation,
    onSaveVehicle,
    onShopProducts,
    showAnimation,
    showChangeVehicleLink = false,
    showEditLink
  } = props;

  const { openModal } = useModal();
  const { getContentSlot } = useCmsPageContentSlots('fitment-messages');
  const { confirmCancelTireWheelPackage, incompletePackage } =
    useTireWheelPackage();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const hasActions = showChangeVehicleLink || onSaveVehicle || onShopProducts;
  const {
    frontTireSize,
    isStaggered,
    make,
    model,
    rearTireSize,
    rideHeight,
    rideHeightType,
    trim,
    year
  } = fitment || {};
  const hasStaggeredFitment = isStaggered && rearTireSize;
  const wrapperMaxSizeClassName = maxSize ? `wrapper--max-${maxSize}` : '';
  let containerStyleName = showAnimation ? 'animated-container' : 'container';

  const handleChangeVehicle = useCallback(async () => {
    if (incompletePackage) {
      await confirmCancelTireWheelPackage();
    } else {
      openModal(VEHICLE_CONFIRMATION);
    }
  }, [confirmCancelTireWheelPackage, incompletePackage, openModal]);

  if (hasActions) {
    containerStyleName += ' container--actions';
  }

  function toggleDeleteConfirmation() {
    onPromptDeleteConfirmation?.();
    setShowDeleteConfirmation(!showDeleteConfirmation);
  }

  function getPartialFitmentMessage() {
    if (incompleteFitmentMessage) return incompleteFitmentMessage;

    const slotKey = trim
      ? 'incomplete-original-tire-size'
      : 'incomplete-trim-and-original-tire-size';

    return (
      getContentSlot(slotKey) ||
      `Your vehicle information has an incomplete ${
        trim ? 'original tire size' : 'trim and original tire size'
      }`
    );
  }

  return (
    <div
      className={`fitment-banner ${className || ''}`}
      styleName={`wrapper ${wrapperMaxSizeClassName}`}
    >
      {showDeleteConfirmation && (
        <Confirmation
          confirmLabel="Yes, delete this vehicle"
          declineLabel="No, keep this vehicle"
          handleConfirm={onDelete}
          handleDecline={toggleDeleteConfirmation}
          hasOverlay
          showSpinner={isDeleteLoading}
        />
      )}
      <div
        className={`fitment-banner__container ${
          className ? `${className}__container` : null
        }`}
        styleName={containerStyleName}
      >
        {!hideVehicleImage && (
          <div
            className="fitment-banner__vehicle-display"
            styleName="vehicle-display"
          >
            <VehicleImage
              alt="vehicle"
              className="fitment-banner__vehicle-image__vehicle-display"
              smallImageUrl={fitment?.smallImageUrl}
              styleName="image"
              vehicleType={fitment?.type}
            />
          </div>
        )}
        <div
          className="fitment-banner__vehicle-description"
          styleName={
            hasStaggeredFitment
              ? 'staggered-vehicle-size-details'
              : 'vehicle-size-details'
          }
        >
          <div
            className="fitment-banner__vehicle-description-size-container"
            styleName="vehicle-description-size-container"
          >
            <div
              className="fitment-banner__vehicle-description"
              styleName="vehicle-description"
            >
              <VehicleDescription
                make={make}
                model={model}
                renderAfterYearMake={() => (
                  <>
                    {showEditLink && (
                      <Button
                        className="fitment-banner__vehicle-description-edit-button"
                        onClick={onEditFitment}
                        styleName={
                          hasStaggeredFitment
                            ? 'edit-vehicle-staggered'
                            : 'edit-vehicle'
                        }
                      >
                        edit
                      </Button>
                    )}
                  </>
                )}
                rideHeight={rideHeight}
                trim={trim}
                year={year}
              />
            </div>

            {!hideFitmentSize && (
              <div
                className="fitment-banner__fitment-size"
                styleName={
                  hasStaggeredFitment
                    ? 'size-details-staggered'
                    : 'size-details'
                }
              >
                {frontTireSize && (
                  <FitmentSizeGroup
                    styleName={isPartialFitment ? 'partial-size-group' : ''}
                  >
                    <FitmentSize
                      isOE={fitment?.isOE}
                      rideHeightType={rideHeightType}
                      sizeQualifier={fitment?.sizeQualifier}
                      tireLocation={hasStaggeredFitment ? 'front' : void 0}
                      tireSize={frontTireSize}
                      wheelDiameter={fitment?.frontWheelDiameter}
                    />

                    {isStaggered &&
                      (Boolean(rearTireSize) ||
                        Boolean(fitment?.rearWheelDiameter)) && (
                        <FitmentSize
                          isOE={fitment?.isOE}
                          rideHeightType={rideHeightType}
                          sizeQualifier={fitment?.sizeQualifier}
                          tireLocation="rear"
                          tireSize={rearTireSize}
                          wheelDiameter={fitment?.rearWheelDiameter}
                        />
                      )}
                  </FitmentSizeGroup>
                )}

                {isPartialFitment && (
                  <Typography
                    size="extraSmall"
                    styleName="incomplete-fitment-message"
                    tag="div"
                  >
                    <BellSolidIcon />
                    {getPartialFitmentMessage()}
                  </Typography>
                )}

                {hasItemsInCart && (
                  <Typography styleName="products-in-cart" tag="div">
                    <ShoppingCartSolidIcon styleName="cart-icon" />
                    Products in cart
                  </Typography>
                )}
              </div>
            )}
          </div>

          <div
            className="fitment-banner__actions"
            styleName={isStaggered ? 'staggered-actions' : 'actions'}
          >
            {showChangeVehicleLink && (
              <Button onClick={handleChangeVehicle} styleName="change-vehicle">
                change vehicle
              </Button>
            )}
            {typeof onShopProducts === 'function' && (
              <Button onClick={onShopProducts}>shop products</Button>
            )}
            {typeof onSaveVehicle === 'function' && (
              <Button
                isDisabled={isSaveVehicleDisabled}
                onClick={onSaveVehicle}
              >
                save vehicle
              </Button>
            )}
          </div>
        </div>
        <div styleName="delete">
          {typeof onPromptDeleteConfirmation === 'function' && (
            <Button
              icon={TrashAltRegularIcon}
              onClick={toggleDeleteConfirmation}
            >
              delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default FitmentBanner;
